import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF } from "@react-google-maps/api";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { LineChart, TrendingUp, BarChart3, Gauge, Star, Award, Users, RefreshCw, Loader2, MapPin, ExternalLink, Ruler } from "lucide-react";
import { GOOGLE_MAPS_LOADER, GOOGLE_MAPS_KEY } from "@/lib/maps";
import MarketPanel from "@/components/MarketPanel";
import ScanProgressModal from "@/components/ScanProgressModal";

const isoDay = (d) => d.toISOString().slice(0, 10);
const defaultCheckIn = () => isoDay(new Date());
const median = (arr) => {
  if (!arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
const mapStyle = { width: "100%", height: "380px", borderRadius: "10px" };

export default function Market() {
  const { lang, properties, activeId } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const active = (properties || []).find((p) => p.id === activeId) || (properties || [])[0];
  const propId = active?.id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [checkIn, setCheckIn] = useState(defaultCheckIn());
  const [nights, setNights] = useState(1);
  const [activeMarker, setActiveMarker] = useState(null);
  const [map, setMap] = useState(null);
  const pollRef = useRef(null);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER);

  const load = useCallback(async () => {
    if (!propId) { setLoading(false); return; }
    try {
      const { data } = await api.get(`/properties/${propId}/market-positioning?lang=${lang}`);
      setData(data);
      // Keep the check-in anchored to TODAY on open (only sync nights from the last window).
      if (data?.window?.nights) setNights(data.window.nights);
    } catch { setData(null); } finally { setLoading(false); }
  }, [propId, lang]);

  useEffect(() => { setLoading(true); load(); return () => pollRef.current && clearInterval(pollRef.current); }, [load]);

  const analyze = async () => {
    setRunning(true);
    try {
      const { data: start } = await api.post(`/properties/${propId}/market-intelligence`, { radius_km: 5, check_in: checkIn, nights, language: lang });
      if (start.status === "ready") { await load(); setRunning(false); toast.success(L("Mercato aggiornato", "Market updated")); return; }
      const ck = start.cache_key;
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/properties/${propId}/market-intelligence/status?cache_key=${ck}`);
          if (st.status === "ready") { clearInterval(pollRef.current); await load(); setRunning(false); toast.success(L("Mercato aggiornato", "Market updated")); }
          else if (st.status === "error") { clearInterval(pollRef.current); setRunning(false); toast.error(L("Errore analisi mercato", "Market analysis error")); }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) { setRunning(false); toast.error(e?.response?.data?.detail || L("Errore", "Error")); }
  };

  const analyzed = data?.analyzed || [];
  // Compare NET-vs-NET: competitor price stripped of city tax + breakfast (room-only) and
  // adjusted by the owner's per-competitor discount %. The backend computes `net_price`;
  // fall back to the gross scraped price only if net is unavailable.
  const netOf = (c) => (c && c.net_price != null ? c.net_price : c?.price_per_night);
  const priced = analyzed.filter((c) => netOf(c) != null && netOf(c) > 0);
  const prices = priced.map(netOf);

  const stats = useMemo(() => {
    const s = data?.stats || {};
    const avg = prices.length ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : (s.avg_price || null);
    const med = s.median_price || (prices.length ? Math.round(median(prices)) : null);
    const min = prices.length ? Math.round(Math.min(...prices)) : (s.min_price || null);
    const max = prices.length ? Math.round(Math.max(...prices)) : (s.max_price || null);
    const rt = priced.filter((c) => c.rating != null).map((c) => c.rating);
    const avgRating = rt.length ? (rt.reduce((a, b) => a + b, 0) / rt.length).toFixed(1) : (s.avg_rating || null);
    const q = priced.filter((c) => c.quality_score != null).map((c) => c.quality_score);
    const avgQ = q.length ? Math.round(q.reduce((a, b) => a + b, 0) / q.length) : (s.avg_quality_score || null);
    return {
      median: med, avg, min, max, avgRating, avgQ,
      pulse: s.market_pulse ?? null, count: analyzed.length, radius: s.radius_km || 5,
    };
  }, [data, prices, priced, analyzed]);

  // price distribution histogram
  const distribution = useMemo(() => {
    if (prices.length < 3) return [];
    const min = Math.min(...prices), max = Math.max(...prices);
    if (max <= min) return [{ range: `€${Math.round(min)}`, count: prices.length }];
    const bins = Math.min(8, Math.max(4, Math.round(Math.sqrt(prices.length))));
    const width = (max - min) / bins;
    const buckets = Array.from({ length: bins }, (_, i) => ({
      lo: min + i * width, hi: min + (i + 1) * width, count: 0,
    }));
    prices.forEach((p) => {
      let idx = Math.floor((p - min) / width);
      if (idx >= bins) idx = bins - 1;
      buckets[idx].count += 1;
    });
    return buckets.map((b) => ({ range: `€${Math.round(b.lo)}–${Math.round(b.hi)}`, count: b.count }));
  }, [prices]);

  const center = useMemo(() => ({ lat: active?.latitude || 41.9, lng: active?.longitude || 12.5 }), [active]);
  const mapPts = analyzed.filter((c) => c.latitude && c.longitude);

  const onMapLoad = useCallback((m) => setMap(m), []);
  useEffect(() => {
    if (map && mapPts.length && window.google) {
      const b = new window.google.maps.LatLngBounds();
      b.extend(center);
      mapPts.forEach((p) => b.extend({ lat: p.latitude, lng: p.longitude }));
      map.fitBounds(b, 60);
    }
  }, [map, mapPts, center]);

  const Stat = ({ icon: Icon, label, value, color }) => (
    <div className="bg-secondary/40 border border-border rounded-lg p-3" data-testid={`market-stat-${label}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground font-bold mb-1"><Icon className="w-3.5 h-3.5" />{label}</div>
      <div className="text-2xl font-data font-bold" style={{ color }}>{value ?? "—"}</div>
    </div>
  );

  return (
    <div className="App min-h-screen"><Nav />
      <ScanProgressModal running={running} lang={lang} />
      <div className="max-w-7xl mx-auto px-6 py-8" data-testid="market-page">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-primary" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{L("Analisi Mercato", "Market analysis")}</h1>
              <p className="text-sm text-muted-foreground">{active ? `${active.name} · ${active.city || active.market?.city || ""}` : L("Seleziona una struttura", "Select a property")}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1">{L("Check-in", "Check-in")}</label>
              <input data-testid="market-checkin" type="date" value={checkIn} min={isoDay(new Date())}
                onChange={(e) => setCheckIn(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-sm font-data" />
            </div>
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1">{L("Notti", "Nights")}</label>
              <select data-testid="market-nights" value={nights} onChange={(e) => setNights(Number(e.target.value))}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm font-data">
                {[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <Button data-testid="market-refresh-btn" size="sm" className="h-9" onClick={analyze} disabled={running || !propId}>
              {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{L("Analisi in corso...", "Analyzing...")}</> : <><RefreshCw className="w-4 h-4 mr-2" />{L("Aggiorna mercato", "Refresh market")}</>}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />{L("Carico i dati di mercato…", "Loading market data…")}</div>
        ) : !active ? (
          <div className="py-24 text-center text-muted-foreground">{L("Nessuna struttura selezionata.", "No property selected.")}</div>
        ) : analyzed.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-lg p-10 text-center" data-testid="market-empty">
            <BarChart3 className="w-7 h-7 text-primary mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-4 max-w-xl mx-auto">{L("Nessun dato di mercato ancora. Analizza i competitor della tua destinazione per vedere distribuzione prezzi, media, mediana, mappa e statistiche.", "No market data yet. Analyze your destination's competitors to see price distribution, average, median, map and statistics.")}</p>
            <Button data-testid="market-analyze-empty" onClick={analyze} disabled={running}>
              {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{L("Analisi in corso...", "Analyzing...")}</> : L("Analizza il mercato", "Analyze the market")}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="market-stats">
              <Stat icon={TrendingUp} label={L("Mediana netta", "Median net")} value={stats.median ? `€${stats.median}` : null} color="var(--primary)" />
              <Stat icon={BarChart3} label={L("Media netta", "Average net")} value={stats.avg ? `€${stats.avg}` : null} />
              <Stat icon={Ruler} label={L("Min netto", "Min net")} value={stats.min ? `€${stats.min}` : null} />
              <Stat icon={Ruler} label={L("Max netto", "Max net")} value={stats.max ? `€${stats.max}` : null} />
              <Stat icon={Gauge} label="Pulse" value={stats.pulse} color="var(--primary)" />
              <Stat icon={Users} label={L("Competitor", "Competitors")} value={stats.count} />
              <Stat icon={Star} label={L("Rating", "Rating")} value={stats.avgRating} />
              <Stat icon={Award} label={L("Qualità", "Quality")} value={stats.avgQ} />
            </div>

            {/* Distribution */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6" data-testid="market-distribution">
              <div className="flex items-center gap-2 mb-1"><LineChart className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">{L("Distribuzione prezzi", "Price distribution")}</h2></div>
              <p className="text-sm text-muted-foreground mb-4">{L(`Come si distribuiscono le tariffe NETTE dei competitor (al netto di tassa, colazione e tua % di sconto) nel raggio di ${stats.radius} km.`, `How competitor NET rates (net of city tax, breakfast and your discount %) spread within ${stats.radius} km.`)}</p>
              {distribution.length ? (
                <div style={{ width: "100%", height: 240 }}>
                  <ResponsiveContainer>
                    <BarChart data={distribution} margin={{ top: 8, right: 8, left: -18, bottom: 4 }}>
                      <XAxis dataKey="range" tick={{ fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={50} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [`${v}`, L("competitor", "competitors")]} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {distribution.map((_, i) => <Cell key={i} fill="var(--primary)" fillOpacity={0.75} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="text-sm text-muted-foreground py-8 text-center">{L("Dati insufficienti per la distribuzione.", "Not enough data for the distribution.")}</p>}
            </motion.div>

            {/* Map */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6" data-testid="market-map">
              <div className="flex items-center gap-2 mb-4"><MapPin className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">{L("Mappa competitor", "Competitor map")}</h2></div>
              {isLoaded && GOOGLE_MAPS_KEY ? (
                <GoogleMap mapContainerStyle={mapStyle} center={center} zoom={13} onLoad={onMapLoad}
                  options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}>
                  <MarkerF position={center} icon={window.google ? { path: window.google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: "#e11d48", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3 } : undefined} title={active?.name} />
                  {mapPts.map((c, i) => (
                    <MarkerF key={i} position={{ lat: c.latitude, lng: c.longitude }} onClick={() => setActiveMarker(i)}
                      icon={window.google ? { path: window.google.maps.SymbolPath.CIRCLE, scale: 7, fillColor: c.selected ? "#2563eb" : "#64748b", fillOpacity: 0.95, strokeColor: "#fff", strokeWeight: 1.5 } : undefined} />
                  ))}
                  {activeMarker != null && mapPts[activeMarker] && (
                    <InfoWindowF position={{ lat: mapPts[activeMarker].latitude, lng: mapPts[activeMarker].longitude }} onCloseClick={() => setActiveMarker(null)}>
                      <div style={{ minWidth: 160 }}>
                        <p style={{ fontWeight: 700, fontSize: 13, margin: 0 }}>{mapPts[activeMarker].name}</p>
                        <p style={{ fontSize: 11, color: "#666", margin: "2px 0" }}>€{netOf(mapPts[activeMarker])} · {mapPts[activeMarker].distance_km} km{mapPts[activeMarker].rating ? ` · ★${mapPts[activeMarker].rating}` : ""}</p>
                      </div>
                    </InfoWindowF>
                  )}
                </GoogleMap>
              ) : (
                <div className="flex items-center justify-center bg-secondary/40 rounded-lg" style={{ height: 380 }}>
                  <p className="text-sm text-muted-foreground">{L("Mappa non disponibile", "Map unavailable")}</p>
                </div>
              )}
            </motion.div>

            {/* Full table */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg overflow-hidden" data-testid="market-table">
              <div className="px-6 py-4 border-b border-border flex items-center gap-2"><ListChecksIcon /><h2 className="text-lg font-semibold">{L("Tabella completa", "Full table")}</h2><span className="text-sm text-muted-foreground">({analyzed.length})</span></div>
              <div className="overflow-x-auto max-h-[520px]">
                <table className="w-full text-sm" data-testid="market-full-table">
                  <thead className="bg-secondary/60 sticky top-0">
                    <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      {[L("Struttura", "Structure"), L("Tipo", "Type"), L("Distanza", "Distance"), L("€/notte netto", "€/night net"), L("Rating", "Rating"), L("Recensioni", "Reviews"), L("Similarità", "Similarity")].map((h) => <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {[...analyzed].sort((a, b) => (a.distance_km ?? 999) - (b.distance_km ?? 999)).map((c, i) => (
                      <tr key={i} data-testid={`market-table-row-${i}`} className={`border-t border-border hover:bg-accent/40 ${c.selected ? "bg-primary/5" : ""}`}>
                        <td className="px-3 py-2 max-w-[240px] truncate">
                          {c.url ? <a href={c.url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">{c.name}<ExternalLink className="w-3 h-3" /></a> : (c.name || "—")}
                          {c.selected && <span className="ml-2 text-[9px] px-1 py-0.5 rounded bg-primary text-primary-foreground uppercase">{L("usato", "used")}</span>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{c.type || "—"}{c.stars ? ` · ${c.stars}★` : ""}</td>
                        <td className="px-3 py-2 text-right font-data whitespace-nowrap">{c.distance_km != null ? `${c.distance_km} km` : "—"}</td>
                        <td className="px-3 py-2 font-data font-semibold whitespace-nowrap" title={c.price_per_night != null ? L(`Lordo rilevato: €${c.price_per_night}`, `Gross scraped: €${c.price_per_night}`) : ""}>{netOf(c) != null ? `€${netOf(c)}` : "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{c.rating != null ? <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 fill-current text-amber-500" />{c.rating}</span> : "—"}</td>
                        <td className="px-3 py-2 font-data">{c.reviews || "—"}</td>
                        <td className="px-3 py-2 font-data">{c.similarity != null ? `${c.similarity}%` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        )}

        {/* Market & competitors positioning (moved here from Dashboard) */}
        {active && propId && (
          <div className="mt-6" data-testid="market-positioning-section">
            <MarketPanel propId={propId} variant="positioning" />
          </div>
        )}
      </div>
    </div>
  );
}

function ListChecksIcon() {
  return <BarChart3 className="w-5 h-5 text-primary" />;
}
